import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MsalBroadcastService,
  MsalService,
} from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import {
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { Router, RouterLink } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'auth-login',
  imports: [RouterLink],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly _destroying$ = new Subject<void>();

  /**
   * Where a signed-in user belongs. Having a bank profile is the server's answer to
   * "are you an active user"; anyone without one goes to /register, which then works
   * out whether they still have to apply, are waiting, or were rejected.
   *
   * Deliberately makes no HTTP call of its own: a freshly signed-up account often has
   * no usable silent token yet, and a 401 here would trigger the interceptor's logout.
   */
  private async routeAfterLogin(): Promise<void> {
    try {
      await this.auth.profileReady();

      if (!this.auth.bankProfile) {
        await this.router.navigate(['/register']);
        return;
      }

      // Retailers have no place in the backoffice; retailerGuard would bounce them anyway
      await this.router.navigate([this.auth.isRetailer ? '/no-access' : '/employees']);
    } catch (error) {
      console.error('Failed to resolve the post-login destination', error);
      await this.router.navigate(['/register']);
    }
  }

  ngOnInit() {
    // Dev impersonation: there is nothing to sign in to
    if (environment.devAuth?.enabled) {
      void this.routeAfterLogin();
      return;
    }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        // Nobody signed in: stay on the login card
        if (this.msalService.instance.getAllAccounts().length === 0) return;

        void this.routeAfterLogin();
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        console.error('Authentication failed', result.error);
      });
    this.msalService.handleRedirectObservable().subscribe(); // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled
  }

  login(request?: RedirectRequest | PopupRequest) {
    this.msalService.loginRedirect(request);
  }

  register() {
    // Trigger B2C signup flow - after successful signup, user will be redirected to /register
    const signUpRequest: RedirectRequest = {
      authority: environment.b2cPolicies.authorities.signUp.authority,
      scopes: environment.apiConfig.scopes,
      redirectUri: '/',
    };
    this.msalService.loginRedirect(signUpRequest);
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}

